import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ControlledModal from "../Modal/ControlledModal";
import { Button } from "../ui/button";
import {
  useCreateLibraryItemMutation,
  useUpdateLibraryItemMutation,
} from "../../reactWrapper/Redux/apiSlice";
import { LIBRARY, KINDS, FIELDS, chain, describeError } from "./library.config";
import ParentSelects from "./ParentSelects";
import { useGetLibraryOptionsQuery } from "../../reactWrapper/Redux/apiSlice";

const inputClass =
  "h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

/**
 * One modal for every kind and for both create and edit.
 *
 *   <LibraryFormModal open mode="create" onClose={...} />
 *   <LibraryFormModal open mode="edit" kind="role" item={row} onClose={...} />
 *
 * Create opens on the kind picker, then the form. Edit opens straight on
 * the form, prefilled, with the parent taken from the row's `chain`.
 *
 * Only the immediate parent is asked for: a skill picks a role, a tool
 * picks a specialty, a role picks its categories. The levels above are
 * already settled by that choice, so putting them on the form would be a
 * second answer to a question that is not being asked.
 *
 * A skill has a "generic" checkbox: ticked, it belongs to no role and the
 * parent dropdown is disabled.
 */
export default function LibraryFormModal({
  open,
  mode = "create",
  kind: initialKind,
  item,
  onClose,
}) {
  const [kind, setKind] = useState(initialKind || null);
  const [parents, setParents] = useState({});
  const [categoryIds, setCategoryIds] = useState([]);
  const [categorySearch, setCategorySearch] = useState("");
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [values, setValues] = useState({});
  const [generic, setGeneric] = useState(false);
  const [error, setError] = useState("");

  const [createItem, { isLoading: creating }] = useCreateLibraryItemMutation();
  const [updateItem, { isLoading: updating }] = useUpdateLibraryItemMutation();
  const busy = creating || updating;

  /* Reset every time the modal opens, and prefill for edit. */
  useEffect(() => {
    if (!open) return;
    setError("");
    if (mode === "edit" && item && initialKind) {
      setKind(initialKind);
      const p = {};
      chain(initialKind).forEach((level) => {
        p[level] =
          item.chain?.[level]?.id ??
          (level === LIBRARY[initialKind].parent ? item.parent_id : null) ??
          null;
      });
      setParents(p);
      setCategoryIds(item.category_ids || (p.category ? [p.category] : []));
      setCategorySearch("");
      setCategoryMenuOpen(false);
      const v = {};
      LIBRARY[initialKind].fields.forEach((f) => {
        v[f] =
          f === "aliases" ? (item.aliases || []).join(", ") : item[f] ?? "";
      });
      setValues(v);
      setGeneric(!!item.is_generic);
    } else {
      setKind(initialKind || null);
      setParents({});
      setCategoryIds([]);
      setCategorySearch("");
      setCategoryMenuOpen(false);
      setValues({});
      setGeneric(false);
    }
  }, [open, mode, item, initialKind]);

  /* On the first render after opening in edit mode the effect above has not
     run yet, so fall back to the prop rather than dereference a null config. */
  const activeKind = kind || (mode === "edit" ? initialKind : null) || null;
  const cfg = activeKind ? LIBRARY[activeKind] : null;
  const parentKind = cfg?.parent || null;
  const canBeGeneric = !!cfg?.generic;
  const isGeneric = canBeGeneric && generic;
  const isRole = activeKind === "role";
  const { data: categoryOptions = [], isFetching: categoriesLoading } =
    useGetLibraryOptionsQuery(
      { kind: "category" },
      { skip: !isRole || isGeneric },
    );
  const filteredCategories = categoryOptions.filter((category) =>
    category.name.toLowerCase().includes(categorySearch.trim().toLowerCase()),
  );
  const selectedCategories = categoryOptions.filter((category) =>
    categoryIds.includes(category.id),
  );

  /* The form asks for the immediate parent only, so the trail shows that
     one rather than a chain of levels nobody filled in. */
  const crumb = useMemo(() => {
    if (!activeKind) return null;
    return chain(activeKind).slice(-1).map((level) => {
      const id = parents[level] ?? null;
      /* Only reuse the row's chain name when it really points at the same
         id. An unassigned parent has no chain entry, so `undefined ===
         undefined` used to pass and then read .name off nothing. */
      const entry = item?.chain?.[level];
      const name =
        mode === "edit" && id && entry && entry.id === id ? entry.name : null;
      return { level, id, name };
    });
  }, [activeKind, parents, mode, item]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    const name = (values.name || "").trim();
    /* A parent is always required, on create and on edit, unless the item
       is a generic skill. Existing roles arrive without a category, and
       saving one is the moment it gets sorted. */
    if (
      parentKind &&
      !isGeneric &&
      (isRole ? !categoryIds.length : !parents[parentKind])
    ) {
      setError(`Choose a ${LIBRARY[parentKind].label.toLowerCase()} first.`);
      return;
    }
    /* One character is a real name: R and C are languages, and a tool or
       skill can be just as short. */
    if (!name.length) return setError("Enter a name.");
    if (name.length > 60)
      return setError("Name is too long (60 characters max).");

    const body = { name };
    if (cfg.fields.includes("description"))
      body.description = values.description || null;
    if (cfg.fields.includes("aliases")) body.aliases = values.aliases || "";
    if (canBeGeneric) body.generic = isGeneric;
    if (parentKind && !isGeneric) {
      body.parentId = isRole ? categoryIds[0] : parents[parentKind];
      if (isRole) body.parentIds = categoryIds;
    }

    try {
      if (mode === "edit") {
        if (
          parentKind &&
          !isGeneric &&
          item.parent_id &&
          item.parent_id !== parents[parentKind]
        )
          body.previousParentId = item.parent_id;
        await updateItem({ kind: activeKind, id: item.id, body }).unwrap();
        toast.success(`${cfg.label} updated`);
      } else {
        const res = await createItem({ kind: activeKind, body }).unwrap();
        /* A skill a second role also claims belongs to neither, so the API
           makes it generic and drops the link it had. Said plainly, or the
           admin adds a skill to a role and finds it under Generic. */
        const saved = res?.data ?? res;
        if (saved?.became_generic)
          toast.success(
            `"${name}" is used by ${(saved.shared_with || []).join(" and ")}, so it is now a generic skill and belongs to no role.`,
          );
        else toast.success(`${cfg.label} "${name}" saved`);
      }
      onClose?.({ saved: true, kind: activeKind });
    } catch (err) {
      setError(describeError(err, activeKind));
    }
  };

  const showPicker = !cfg;

  return (
    <ControlledModal
      open={open}
      closeModal={() => onClose?.()}
      closeOnDocumentClick={!busy}
      contentStyle={{
        width: "min(560px, 94vw)",
        borderRadius: "12px",
        padding: 0,
        border: "none",
        background: "transparent",
      }}>
      <form
        onSubmit={submit}
        className="flex max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-card">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-bold text-brand-navy">
            {showPicker
              ? "What are you uploading?"
              : mode === "edit"
              ? `Edit ${cfg.label.toLowerCase()}`
              : `New ${cfg.label.toLowerCase()}`}
          </h2>
          <button
            type="button"
            onClick={() => onClose?.()}
            className="text-sm text-muted-foreground hover:text-foreground"
            disabled={busy}>
            Close
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5">
          {showPicker ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setKind(k);
                    setError("");
                  }}
                  className="rounded-lg border border-border bg-background p-3 text-left transition-colors hover:border-brand-navy focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                  <span className="block font-semibold text-brand-navy">
                    {LIBRARY[k].label}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {LIBRARY[k].hint}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {crumb?.length ? (
                <p className="text-xs text-muted-foreground">
                  {isGeneric ? (
                    <span>generic (no role)</span>
                  ) : (
                    crumb.map((c, i) => (
                      <span key={c.level}>
                        {i > 0 ? " → " : ""}
                        {c.id ? (
                          <b className="text-foreground">
                            {c.name || LIBRARY[c.level].label}
                          </b>
                        ) : (
                          LIBRARY[c.level].label.toLowerCase()
                        )}
                      </span>
                    ))
                  )}
                  {" → "}
                  <b className="text-foreground">
                    {mode === "edit"
                      ? values.name || item?.name
                      : `new ${cfg.label.toLowerCase()}`}
                  </b>
                </p>
              ) : null}

              {isRole ? (
                <fieldset className="flex flex-col gap-2">
                  <legend className="text-xs font-semibold text-muted-foreground">
                    Categories
                  </legend>
                  <div className="relative">
                    <button
                      type="button"
                      className={`${inputClass} flex items-center justify-between text-left`}
                      onClick={() => setCategoryMenuOpen((open) => !open)}
                      disabled={isGeneric || categoriesLoading}>
                      <span className="text-muted-foreground">
                        {categoriesLoading
                          ? "Loading categories…"
                          : "Select categories"}
                      </span>
                      <span className="text-xs text-muted-foreground">▼</span>
                    </button>
                    {categoryMenuOpen ? (
                      <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-card p-2 shadow-lg">
                        <input
                          type="search"
                          value={categorySearch}
                          onChange={(e) => setCategorySearch(e.target.value)}
                          placeholder="Search categories"
                          className={inputClass}
                          autoFocus
                        />
                        <div className="mt-2 max-h-48 overflow-y-auto">
                          {filteredCategories.length ? (
                            filteredCategories.map((category) => (
                              <button
                                key={category.id}
                                type="button"
                                className="flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm hover:bg-accent"
                                onClick={() => {
                                  setCategoryIds(
                                    categoryIds.includes(category.id)
                                      ? categoryIds.filter(
                                          (id) => id !== category.id,
                                        )
                                      : [...categoryIds, category.id],
                                  );
                                }}>
                                <span>{category.name}</span>
                                {categoryIds.includes(category.id) ? (
                                  <span className="text-brand-navy">✓</span>
                                ) : null}
                              </button>
                            ))
                          ) : (
                            <p className="px-2 py-3 text-sm text-muted-foreground">
                              No categories found.
                            </p>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedCategories.map((category) => (
                      <span
                        key={category.id}
                        className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800">
                        {category.name}
                        <button
                          type="button"
                          aria-label={`Remove ${category.name}`}
                          className="ml-1 text-blue-700 hover:text-blue-950"
                          onClick={() =>
                            setCategoryIds(
                              categoryIds.filter((id) => id !== category.id),
                            )
                          }>
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </fieldset>
              ) : (
                <ParentSelects
                  kind={activeKind}
                  direct
                  value={parents}
                  onChange={(p) => {
                    setParents(p);
                    setError("");
                  }}
                  mode="form"
                  idPrefix="form"
                  disabled={isGeneric}
                />
              )}

              {canBeGeneric ? (
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={isGeneric}
                    onChange={(e) => {
                      setGeneric(e.target.checked);
                      setError("");
                    }}
                  />
                  <span>
                    <span className="font-medium">Generic skill</span>
                    <span className="block text-xs text-muted-foreground">
                      Not tied to any role, e.g. Communication or Teamwork.
                      {mode === "edit" && !item?.is_generic
                        ? " Ticking this removes the skill from every role it is under."
                        : ""}
                    </span>
                  </span>
                </label>
              ) : null}

              {cfg.fields.map((f) => {
                const F = FIELDS[f];
                return (
                  <div key={f} className="flex flex-col gap-1.5">
                    <label
                      htmlFor={`form-${f}`}
                      className="text-xs font-semibold text-muted-foreground">
                      {F.label}
                      {F.required ? (
                        ""
                      ) : (
                        <span className="font-normal"> (optional)</span>
                      )}
                    </label>
                    <input
                      id={`form-${f}`}
                      type="text"
                      className={inputClass}
                      placeholder={F.placeholder}
                      value={values[f] ?? ""}
                      onChange={(e) =>
                        setValues({ ...values, [f]: e.target.value })
                      }
                      autoFocus={f === "name"}
                    />
                  </div>
                );
              })}

              {error ? (
                <p
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </p>
              ) : null}
            </div>
          )}
        </div>

        {!showPicker ? (
          <div className="flex items-center justify-between gap-2 border-t border-border px-6 py-4">
            {mode === "create" ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setKind(null);
                  setParents({});
                  setGeneric(false);
                  setError("");
                }}
                disabled={busy}>
                Back
              </Button>
            ) : (
              <span />
            )}
            <Button
              type="submit"
              disabled={busy}
              className="bg-brand-navy text-white hover:bg-brand-navy/90">
              {busy
                ? "Saving…"
                : mode === "edit"
                ? "Save changes"
                : `Save ${cfg.label.toLowerCase()}`}
            </Button>
          </div>
        ) : null}
      </form>
    </ControlledModal>
  );
}
